/* Tiny helpers shared by every auth page.
 *
 * - bootShell() wires the language switcher and matrix rain so every page
 *   gets the same chrome behavior without duplicating boilerplate.
 * - showError / showSuccess / clearMessages target standard `.auth-error`
 *   and `.auth-success` slots in the page markup.
 * - toast() pops a transient message in the bottom-centered `#toast` element.
 */

import { attachBackgroundPainter } from '../../modules/background-painter';
import { applyPersistedTheme, attachThemePickerHandlers } from '../../modules/theme-picker';
import { attachLangSwitcher, applyLang } from '../../modules/i18n';

export function bootShell(): void {
  // Order matters: theme attribute set BEFORE the painter so the right
  // painter is chosen on first frame.
  applyPersistedTheme();
  attachBackgroundPainter();
  attachThemePickerHandlers();
  attachLangSwitcher();
  applyLang();
}

export function showError(msg: string): void {
  clearMessages();
  const el = document.getElementById('auth-error');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
}

export function showSuccess(msg: string): void {
  clearMessages();
  const el = document.getElementById('auth-success');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
}

export function clearMessages(): void {
  const err = document.getElementById('auth-error');
  const ok = document.getElementById('auth-success');
  if (err) { err.classList.remove('show'); err.textContent = ''; }
  if (ok) { ok.classList.remove('show'); ok.textContent = ''; }
}

let toastTimer: number | null = null;
export function toast(msg: string, durationMs = 2800): void {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el.classList.remove('show'), durationMs);
}

/** Friendly message for AuthError-style errors raised by the auth client. */
export function describeError(err: any, fallback = 'Something went wrong. Try again.'): string {
  if (!err) return fallback;
  const status = err.status ?? 0;
  const raw = String(err.message || '').trim();
  switch (status) {
    case 400: return raw || 'Check your input and try again.';
    case 401: return 'Wrong email or password.';
    case 403: return raw || 'You do not have permission for that.';
    case 404: return raw || 'Not found.';
    case 409: return raw || 'That email is already registered.';
    case 422: return raw || 'Some fields look off. Check and try again.';
    case 429: return 'Too many attempts. Slow down and try again in a minute.';
    case 503: return 'Service temporarily unavailable.';
    default:
      if (status >= 500) return 'Server hiccup. Please retry in a moment.';
      return raw || fallback;
  }
}
